import React, {useEffect, useMemo, useState} from "react";
import {collection, doc, getDocs, onSnapshot} from "firebase/firestore";
import db from "../../../firebase";
import styles from "./TaskManagerModal.module.css";
import {
    BucketTaskDraft,
    saveBucketSetup,
} from "../../../roomActions";
import IconClose from "assets/icon-close.svg";
import IconList from "assets/icon-list.svg";
import IconChevUp from "assets/icon-chevron-up.svg";
import IconChevDown from "assets/icon-chevron-down.svg";
import IconTrash from "assets/icon-trash.svg";
import { useTranslation } from "react-i18next";
import CsvImportPanel, { CsvImportResult } from "../CsvImportPanel/CsvImportPanel";

type Props = {
    roomId: string;
    onClose: () => void;
};

type SetupStatus = "draft" | "ready" | "inProgress" | "finished";

type TaskRow = Required<BucketTaskDraft> & {
    iterationId?: string | null;
    iterationIndex?: number | null;
};

type AddMode =
  | "idle"
  | "manual"
  | "csv";

const tasksPerIterationOptions = [2, 3, 4];

const TaskManagerModal: React.FC<Props> = ({roomId, onClose}) => {
    const { t } = useTranslation();
    const [setupStatus, setSetupStatus] = useState<SetupStatus>("draft");
    const [tasks, setTasks] = useState<TaskRow[]>([]);
    const [tasksPerIter, setTasksPerIter] = useState<number>(4);
    const [hasChanges, setHasChanges] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState<string | null>(null);
    const [addMode, setAddMode] = useState<AddMode>("idle");

    // подтягиваем текущие tasks/состояние, если уже что-то есть
    useEffect(() => {
        const unsub = onSnapshot(doc(db, "rooms", roomId), (snap) => {
            if (!snap.exists()) return;
            const d = snap.data() as any;
            setSetupStatus((d.setupStatus as SetupStatus) ?? "draft");
            if (d.tasksPerIteration) setTasksPerIter(d.tasksPerIteration);
        });

        (async () => {
            const s = await getDocs(collection(db, "rooms", roomId, "tasks"));
            const rows = s.docs.map((d) => ({
                id: d.id,
                ...(d.data() as any),
            })) as TaskRow[];
            rows.sort((a, b) => {
                const ai = (a.iterationId ? parseInt(a.iterationId) : 0) * 1000 + (a.iterationIndex ?? 0);
                const bi = (b.iterationId ? parseInt(b.iterationId) : 0) * 1000 + (b.iterationIndex ?? 0);
                return ai - bi;
            });
            setTasks(rows);
        })();

        return () => unsub();
    }, [roomId]);

    // локальная группировка по итерациям для UI предпросмотра
    const groups = useMemo(() => {
        // если задач нет — группируем по текущему значению tasksPerIter
        const list = tasks.length ? tasks : [];
        const chunked: TaskRow[][] = [];
        for (let i = 0; i < list.length; i += tasksPerIter) {
            chunked.push(list.slice(i, i + tasksPerIter));
        }
        if (!list.length) chunked.push([]);
        return chunked;
    }, [tasks, tasksPerIter]);

    const addTaskTitle = useMemo(() => {
        return t(`addTaskTitle.${addMode}`);
    }, [addMode]);

    const addTask = () => {
        if (!title.trim()) return;
        setTasks((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                title: title.trim(),
                description: description.trim(),
                iterationId: null,
                iterationIndex: null,
            },
        ]);
        setHasChanges(true);
        setTitle("");
        setDescription("");
    };

    const getGlobalIndex = (iterIdx: number, taskIdx: number) => {
        let global = 0;
        for (let i = 0; i < iterIdx; i++) global += groups[i].length;
        return global + taskIdx;
    };

    const moveOne = (iterIdx: number, taskIdx: number, dir: "up" | "down") => {
        const flat = groups.flat();
        const current = getGlobalIndex(iterIdx, taskIdx);
        const target = dir === "up" ? current - 1 : current + 1;

        if (target < 0 || target >= flat.length) return;

        const copy = [...flat];
        [copy[current], copy[target]] = [copy[target], copy[current]];
        setTasks(copy);
        setHasChanges(true);
    };

    const removeAt = (iterIdx: number, taskIdx: number) => {
        const flat = groups.flat();
        const current = getGlobalIndex(iterIdx, taskIdx);
        const copy = [...flat];
        copy.splice(current, 1);
        setTasks(copy);
        setHasChanges(true);
    };

    const handleSave = async () => {
        // Готовим плоский список без iterationId — серверная раскладка по N
        const drafts = tasks.map((t) => ({id: t.id, title: t.title, description: t.description}));
        await saveBucketSetup({roomId, tasks: drafts, tasksPerIteration: tasksPerIter});
        setHasChanges(false);
        onClose();
    };

    const handleCsvApply = (result: CsvImportResult) => {
        const newTasks: TaskRow[] = result.tasks.map(t => ({
            id: crypto.randomUUID(),
            title: t.title,
            description: t.description ?? "",
            iterationId: null,
            iterationIndex: null,
        }));

        setTasks(prev => [...prev, ...newTasks]);
        setHasChanges(true);
        // setAddMode("idle");
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalPanel}>
                <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z"
                            fill="#1D1B20"/>
                    </svg>
                </button>
                <div className={styles.modalTitle}>{t("taskManagement")}</div>

                {/* Добавление задачи */}
                <div className={styles.form}>
                    <div className={styles.modalSubTitle}>
                        {addTaskTitle}
                    </div>

                    {addMode === "idle" && (
                        <div className={styles.addModeButtons}>
                            <button
                                className={`${styles.btn} ${styles.btnSecondary}`}
                                onClick={() => setAddMode("manual")}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2.91667 11.0833H3.74792L9.45 5.38125L8.61875 4.55L2.91667 10.2521V11.0833ZM1.75 12.25V9.77083L9.45 2.08542C9.56667 1.97847 9.69549 1.89583 9.83646 1.8375C9.97743 1.77917 10.1257 1.75 10.2812 1.75C10.4368 1.75 10.5875 1.77917 10.7333 1.8375C10.8792 1.89583 11.0056 1.98333 11.1125 2.1L11.9146 2.91667C12.0313 3.02361 12.1163 3.15 12.1698 3.29583C12.2233 3.44167 12.25 3.5875 12.25 3.73333C12.25 3.88889 12.2233 4.03715 12.1698 4.17812C12.1163 4.3191 12.0313 4.44792 11.9146 4.56458L4.22917 12.25H1.75ZM9.02708 4.97292L8.61875 4.55L9.45 5.38125L9.02708 4.97292Z" fill="#460B74"/>
                                </svg>
                                {t("addManually")}
                            </button>

                            <button
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                onClick={() => setAddMode("csv")}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6.41732 9.33203V4.57786L4.90065 6.09453L4.08398 5.2487L7.00065 2.33203L9.91732 5.2487L9.10065 6.09453L7.58398 4.57786V9.33203H6.41732ZM3.50065 11.6654C3.17982 11.6654 2.90516 11.5511 2.67669 11.3227C2.44822 11.0942 2.33398 10.8195 2.33398 10.4987V8.7487H3.50065V10.4987H10.5007V8.7487H11.6673V10.4987C11.6673 10.8195 11.5531 11.0942 11.3246 11.3227C11.0961 11.5511 10.8215 11.6654 10.5007 11.6654H3.50065Z" fill="#5A4E00"/>
                                </svg>
                                {t("importCsv")}
                            </button>
                        </div>
                    )}

                    {addMode === "manual" && (
                        <>
                            <input
                                placeholder={t("taskNamePlaceholder")}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                            <textarea
                                placeholder={t("taskDescriptionPlaceholder")}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={3}
                            />
                            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={addTask}>
                                <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M4.41675 5.08268H0.916748V3.91602H4.41675V0.416016H5.58341V3.91602H9.08341V5.08268H5.58341V8.58268H4.41675V5.08268Z"
                                        fill="#460B74" />
                                </svg>
                                {t("addTaskToList")}
                            </button>
                        </>
                    )}

                    {addMode === "csv" && (
                        <CsvImportPanel onApply={handleCsvApply} />
                    )}
                </div>

                {/* Список задач + настройки итераций */}
                <div className={styles.toolbar}>
                    <div className={`${styles.modalSubTitle} ${styles.toolbarTitle}`}>{t("allTasks")} ({tasks.length})
                    </div>
                    <div className={styles.toolbarRight}>
                        <select
                            className={styles.btn}
                            value={tasksPerIter}
                            onChange={(e) => {
                                setTasksPerIter(parseInt(e.target.value, 10));
                                setHasChanges(true);
                            }}
                        >
                            {tasksPerIterationOptions.map((n) => (
                                <option key={n} value={n}>{t("taskPerIteration", { count: n })}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.iterations}>
                        {tasks.length > 0 && groups.map((g, i) => (
                            <div key={i} className={styles.iteration}>
                                <div className={styles.iterationTitle}>
                                    <svg width="16" height="12" viewBox="0 0 16 12" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M5 1.5H14.75M5 6H14.75M5 10.5H14.75M1.25 1.5H1.2575M1.25 6H1.2575M1.25 10.5H1.2575"
                                            stroke="#A8A8A8" strokeWidth="1.5" strokeLinecap="round"
                                            strokeLinejoin="round"/>
                                    </svg>
                                    {t("iteration", {index: i + 1})}
                                </div>
                                <div className={styles.iterationList}>
                                    {g.map((task, idx) => (
                                        <div
                                            key={task.id}
                                            className={styles.taskRow}
                                            onMouseEnter={() => setHoveredTaskId(task.id)}
                                            onMouseLeave={() => setHoveredTaskId((prev) => (prev === task.id ? null : prev))}
                                        >
                                            <div className={styles.taskIndex}>{idx + 1}</div>
                                            <div className={styles.taskMain}>
                                                <div className={styles.taskTitle}>{task.title}</div>
                                                <button
                                                    className={`${isVisible == task.id ? styles.hideDescBtnActive : ''} ${styles.hideDescBtn}`}
                                                    onClick={() => setIsVisible((prev) => (prev === task.id ? null : task.id))}
                                                    aria-label={isVisible == task.id ? t("collapse") : t("expand")}
                                                >
                                                    {isVisible == task.id ? t("collapse") : t("expand")}
                                                </button>
                                                {isVisible == task.id && (
                                                    <div className={styles.taskDesc}>{task.description}</div>
                                                )}
                                            </div>
                                            <div className={styles.taskActions}>
                                                <button
                                                    className={styles.btn}
                                                    onClick={() => moveOne(i, idx, "up")}
                                                    title={t("moveUp")}
                                                    aria-label={t("moveUp")}
                                                >
                                                    <img src={IconChevUp} alt="↑"/>
                                                </button>
                                                <button
                                                    className={styles.btn}
                                                    onClick={() => moveOne(i, idx, "down")}
                                                    title={t("moveDown")}
                                                    aria-label={t("moveDown")}
                                                >
                                                    <img src={IconChevDown} alt="↓"/>
                                                </button>
                                                <button
                                                    className={styles.btn}
                                                    onClick={() => removeAt(i, idx)}
                                                    title={t("deleteTaskButton")}
                                                    aria-label={t("deleteTaskButton")}
                                                >
                                                    <img src={IconTrash} alt="🗑"/>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                <div className={styles.footer}>
                    <button
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={handleSave}
                        disabled={!hasChanges}
                    >
                        {t("saveChanges")}
                    </button>
                    <button className={`${styles.btn} ${styles.btnGhost}`} onClick={onClose}>{t("cancel")}</button>
                </div>
            </div>
        </div>
    );
};

export default TaskManagerModal;
